import { RouterProvider } from 'react-router-dom';
import { Toaster } from './components/ui/toast/toaster.tsx';
import { useMount } from '@/lib/hooks/mount/use-mount.hook.tsx';
import { languageService } from '@/services/language/language.service.ts';
import i18n from '@/i18n.ts';
import dayjs from 'dayjs';

import 'leaflet/dist/leaflet.css';
import { routerConfig } from '@/route/router.config.tsx';
import React, { useEffect } from 'react';
import { LoadingScreen } from './components/ui/loading-screen/loading-screen.tsx';
import { useAppVersion } from '@/lib/hooks/app-version/app-version.hook.tsx';
import { toast } from '@/components/ui/toast/use-toast.ts';
import { Button } from '@/components/ui/button/button.tsx';
import { AppIcon, appIcons } from '@/components/ui/app-icon/app-icon.tsx';
import { clsx } from '@/lib/utils.ts';
import { useTranslation } from 'react-i18next';
import { sentryCreateBrowserRouter } from '@/instrument.ts';
import { useAuth } from 'react-oidc-context';
import isNil from 'lodash.isnil';
import { useAuthStore } from '@/store/auth.store.ts';
import { stripOidcParams } from '@/lib/oidc/oidc.util.ts';

const router = sentryCreateBrowserRouter(routerConfig);

function App() {
    const auth = useAuth();
    const [t] = useTranslation();
    const token = useAuthStore((state) => state.token);

    // force app language to German
    useMount(() => {
        // use German as the default language not matter the browser settings
        const locale = languageService.forceGermanLanguage();
        void i18n.changeLanguage(locale);
        dayjs.locale(locale);
    });

    const { newVersionAvailable } = useAppVersion(10000);

    useEffect(() => {
        if (newVersionAvailable)
            toast({
                title: t('general.appUpdate'),
                description: t('general.appUpdate.reload'),
                action: (
                    <Button
                        onClick={() => window.location.reload()}
                        className={clsx('h-8', 'w-10')}>
                        <AppIcon icon={appIcons.refresh} size={18} />
                    </Button>
                ),
                duration: Infinity,
            });
    }, [newVersionAvailable, t]);

    useEffect(() => {
        if (auth.user) {
            useAuthStore.getState().setAuthToken(auth.user.access_token);
        }
    }, [auth.user]);
    /** If the user is not logged in navigate to login page */
    useEffect(() => {
        if ((!auth.isLoading && !auth.isAuthenticated) || (isNil(token) && !auth.isLoading)) {
            void auth.signinRedirect({
                redirect_uri: stripOidcParams(window.location.href),
            });
        }
    }, [auth, token]);

    return (
        <React.Fragment>
            <React.Suspense fallback={<LoadingScreen />}>
                <RouterProvider router={router} />
            </React.Suspense>
            <Toaster />
        </React.Fragment>
    );
}

export default App;
